import React, { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import dayjs from "dayjs";
import { PrintRE } from "../print/PrintRE";

function fmt(v) {
  if (v == null) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v));
}

// ─── Pending payment card (ยังไม่ได้ยืนยัน) ───────────────────
function PendingPaymentCard({ pending, index, onEdit, onRemove }) {
  const total = Number(pending.net_paid_amount) || 0;
  const paymentDate = pending.payment_date
    ? dayjs(pending.payment_date).format("DD/MM/YYYY")
    : "-";

  const splits = pending.splits ?? [];

  return (
    <div className="mb-3 mt-0 shadow bg-white rounded-xl md:p-6 p-4 overflow-hidden max-w-[1000px]">
      <div className="mb-3 text-sm text-[#4C4E64]/[0.68]">การรับชำระเงิน</div>
      <div className="flex items-center">
        <div className="text-[#242F48] text-[18px] font-medium flex items-center">
          <HourglassTopIcon className="!text-[#FF6B00] !mr-2" />
          ชำระเงินครั้งที่ {index + 1}
        </div>
        <div
          className="ml-3 text-xs font-medium rounded-full px-3 py-0.5"
          style={{ background: "#FFF3E0", color: "#FF6B00" }}
        >
          รอยืนยัน
        </div>

        {onEdit && (
          <IconButton
            aria-label="edit-pending"
            size="small"
            className="!bg-[#E3E8F0] !ml-4 !mr-2"
            onClick={() => onEdit(pending)}
          >
            <EditIcon className="!text-[#93A1B8] !text-sm" />
          </IconButton>
        )}
        {onRemove && (
          <IconButton
            aria-label="remove-pending"
            className="!bg-[#F95057]"
            size="small"
            onClick={onRemove}
          >
            <DeleteIcon className="!text-[#FFF] !text-sm" />
          </IconButton>
        )}

        <div className="ml-auto text-sm text-[#4C4E64]/[0.68]">
          วันที่ชำระเงิน {paymentDate}
        </div>
      </div>
      <hr className="my-3" />

      <div className="grid grid-cols-4 gap-5 items-end">
        <div className="col-span-2">
          {splits.length > 0 ? (
            splits.map((s, i) => (
              <div key={i} className="text-[#4C4E63] mb-2">
                <div className="flex items-center gap-1">
                  <div className="flex-1">{s.paymentMethodName ?? "-"}</div>
                  <div className="flex-shrink-0">{fmt(s.amount)} บาท</div>
                </div>
                {s._bankName && (
                  <div className="flex items-start gap-2 mt-[4px] ml-[8px]">
                    {s._bankImagePath ? (
                      <img
                        src={s._bankImagePath}
                        alt={s._bankName}
                        style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 2 }}
                      />
                    ) : (
                      <div style={{ width: 20, height: 20, flexShrink: 0 }} />
                    )}
                    <div className="text-[14px] leading-[1.2] text-[#4C4E63]/70">
                      <div>{s._bankName}{s._bankBranchName ? ` (${s._bankBranchName})` : ""}</div>
                      {s._bankAccountNumber && <div>{s._bankAccountNumber}</div>}
                      {s._bankAccountName && <div>{s._bankAccountName}</div>}
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-[#4C4E63]/[0.5] text-sm">ไม่มีรายการชำระ</div>
          )}
        </div>

        <div className="col-span-2">
          <div className="flex justify-end items-end mb-3">
            <div className="text-[#626477] mr-4">มูลค่าที่รับชำระ</div>
            <div
              className="text-white rounded-lg px-2 py-3 text-right min-w-[200px]"
              style={{ background: "#FF6B00" }}
            >
              <span className="font-bold text-[24px]">{fmt(total)} </span>
              <span>บาท</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Saved payment card ───────────────────────────────────────
function PaymentReceiptCard({
  receipt,
  index,
  onEdit,
  onDelete,
  onIssueReceipt,
  onCancelReceipt,
  invoice,
}) {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `receipt-${
      receipt?.memberArPaymentReceipt?.document_no ?? index + 1
    }`,
    onAfterPrint: () => console.log("Printing completed"),
  });

  const receiptData = receipt?.memberArPaymentReceipt ?? {};
  const splits = receipt?.splits ?? [];
  // ใช้ net_paid_amount จาก backend (splits + fee adjustments - WHT)
  // แทนการคำนวณจาก splits อย่างเดียว เพื่อรองรับกรณีไม่มี splits
  const receiptTotal = Number(receipt?.net_paid_amount) || 0;
  const hasReceipt = receipt?.is_receipt_created && !!receiptData?.document_no;
  const paymentDate = receipt?.completed_at
    ? dayjs(receipt.completed_at).format("DD/MM/YYYY")
    : receiptData?.created_at
      ? dayjs(receiptData.created_at).format("DD/MM/YYYY")
      : "-";

  return (
    <div className="mb-3 mt-0 shadow bg-white rounded-xl md:p-6 p-4 overflow-hidden max-w-[1000px]">
      <div className="mb-3">การรับชำระเงิน</div>
      <div className="flex items-center">
        <div className="text-[#242F48] text-[18px] font-medium flex items-center">
          <CheckCircleIcon className="!text-[#2DC76D] !mr-2" />
          ชำระเงินครั้งที่ {index + 1}
        </div>

        <div>
          {onEdit && (
            <IconButton
              aria-label="edit"
              size="small"
              className="!bg-[#E3E8F0] !ml-4 !mr-2"
              onClick={() => onEdit(receipt)}
            >
              <EditIcon className="!text-[#93A1B8] !text-sm" />
            </IconButton>
          )}
          {onDelete && (
            <IconButton
              aria-label="delete"
              className="!bg-[#F95057]"
              size="small"
              onClick={() => onDelete(receipt)}
            >
              <DeleteIcon className="!text-[#FFF] !text-sm" />
            </IconButton>
          )}
        </div>

        <div className="ml-auto text-sm text-[#4C4E64]/[0.68]">
          วันที่ชำระเงิน {paymentDate}
        </div>
      </div>
      <hr className="my-3" />

      <div className="grid grid-cols-4 gap-5 items-end">
        <div className="col-span-2">
          {splits.map((split, i) => {
            const bank = split?.branchBankSetting;
            const bankName = bank?.bank?.name ?? null;
            const branchName = bank?.branch_name ?? null;
            const accountNumber = bank?.account_number ?? null;
            const accountName = bank?.account_name ?? null;
            const bankImagePath = bank?.bank?.image_path ?? null;
            return (
              <div key={i} className="text-[#4C4E63] mb-2">
                <div className="flex items-center gap-1">
                  <div className="flex-1">{split?.paymentMethod?.name ?? "-"}</div>
                  <div className="flex-shrink-0">{fmt(split.amount)} บาท</div>
                </div>
                {bank && (
                  <div className="flex items-start gap-2 mt-[4px] ml-[8px]">
                    {bankImagePath ? (
                      <img
                        src={bankImagePath}
                        alt={bankName}
                        style={{ width: 20, height: 20, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 2 }}
                      />
                    ) : (
                      <div style={{ width: 20, height: 20, flexShrink: 0 }} />
                    )}
                    <div className="text-[14px] leading-[1.2] text-[#4C4E63]/70">
                      <div>{bankName}{branchName ? ` (${branchName})` : ""}</div>
                      {accountNumber && <div>{accountNumber}</div>}
                      {accountName && <div>{accountName}</div>}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="py-3" />
        </div>

        <div className="col-span-2">
          <div className="flex justify-end items-end mb-3">
            <div className="text-[#626477] mr-4">มูลค่าที่รับชำระ</div>
            <div>
              <div className="bg-[#2DC76D] text-white rounded-lg px-2 py-3 text-right min-w-[200px]">
                <span className="font-bold text-[24px]">
                  {fmt(receiptTotal)}{" "}
                </span>
                <span>บาท</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end items-end mb-3">
            {hasReceipt ? (
              <div className="text-[#2DC76D] mr-4">ออกใบเสร็จแล้ว</div>
            ) : (
              <div className="text-[#FF0000] mr-4">ยังไม่ได้ออกใบเสร็จ</div>
            )}
            <div className="text-right flex justify-end gap-2">
              <div style={{ display: "none" }}>
                <PrintRE
                  innerRef={componentRef}
                  receipt={receipt}
                  invoice={invoice}
                />
              </div>
              {hasReceipt ? (
                <>
                  <Button
                    variant="contained"
                    color="blue"
                    className="!px-5"
                    onClick={handlePrint}
                  >
                    พิมพ์ใบเสร็จ
                  </Button>
                  {onCancelReceipt && (
                    <Button
                      variant="outlined"
                      color="error"
                      className="!px-5"
                      onClick={() => onCancelReceipt(receipt)}
                    >
                      ยกเลิกใบเสร็จ
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="contained"
                  color="warning"
                  className="!px-5"
                  onClick={() => onIssueReceipt && onIssueReceipt(receipt)}
                >
                  ออกใบเสร็จ
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Payment({
  modal,
  setModal,
  invoice,
  onEdit,
  onDelete,
  onOpenCreate,
  onIssueReceipt,
  onCancelReceipt,
  pendingPayments = [], // รายการที่รอยืนยัน (local state)
  onEditPending, // แก้ไข pending ก่อนยืนยัน
  onRemovePending, // ลบ pending ออกก่อนยืนยัน
}) {
  const grandTotal = invoice ? Number(invoice.grand_total) : 0;
  const paidAmount = invoice ? Number(invoice.paid_amount) : 0;
  // รวมยอด pending (รอยืนยัน) เข้าไปด้วย เพื่อแสดงยอดคงเหลือที่ถูกต้อง
  const pendingTotal = pendingPayments.reduce(
    (sum, p) => sum + (Number(p.net_paid_amount) || 0),
    0,
  );
  const remainingAmount = grandTotal - paidAmount - pendingTotal;

  const paymentReceiptInvoices = invoice?.paymentReceiptInvoices ?? [];
  const paidCount = paymentReceiptInvoices.length;

  const isPaid = invoice?.status === "PAID";
  const canPay = invoice?.status === "AWAITING_PAYMENT";

  const dueDate = invoice?.due_date
    ? dayjs(invoice.due_date).format("DD/MM/YYYY")
    : "-";

  return (
    <>
      {/* Invoice summary card */}
      <div className="mb-3 mt-0 shadow bg-white rounded-xl md:p-6 p-4 overflow-hidden max-w-[1000px]">
        <div className="flex">
          <div className="text-[#242F48] text-[20px] font-medium">
            ข้อมูลการรับชำระใบแจ้งหนี้เลขที่ {invoice?.document_no ?? "-"}
          </div>
          <div className="ml-auto text-sm text-[#4C4E64]/[0.68]">
            <div>อ้างอิง</div>
            <div>{invoice?.reference ?? "-"}</div>
          </div>
        </div>
        <hr className="my-3" />

        <div className="grid grid-cols-4 gap-5">
          <div className="col-span-2">
            <div className="grid grid-cols-4 text-[#626477] font-medium mb-3">
              <div className="col-span-2">มูลค่าเต็มใบแจ้งหนี้ :</div>
              <div className="col-span-2 text-right">{fmt(grandTotal)} บาท</div>
            </div>
            <div className="grid grid-cols-4 text-[#626477] font-medium mb-3">
              <div className="col-span-2">มูลค่าสุทธิที่ต้องชำระทั้งสิ้น :</div>
              <div className="col-span-2 text-right text-[#2DC76D]">
                {fmt(grandTotal)} บาท
              </div>
            </div>
            <div className="grid grid-cols-4 text-[#626477] font-medium mb-3">
              <div className="col-span-2">
                มูลค่าที่ชำระแล้ว {paidCount + pendingPayments.length} รายการ
                {pendingPayments.length > 0 && (
                  <span
                    className="ml-1 text-xs font-normal"
                    style={{ color: "#FF6B00" }}
                  >
                    (รอยืนยัน {pendingPayments.length})
                  </span>
                )}{" "}
                :
              </div>
              <div className="col-span-2 text-right">
                {fmt(paidAmount + pendingTotal)} บาท
              </div>
            </div>
          </div>

          <div className="col-span-2">
            {isPaid ? (
              <div className="grid grid-cols-4 text-[#626477] flex-end mb-2">
                <div className="col-span-4 text-danger text-right">
                  <div className="text-[#2DC76D] text-[22px] justify-end font-bold flex items-center">
                    <CheckCircleIcon className="!text-[#2DC76D] !mr-2" />
                    ชำระเต็มจำนวนแล้ว
                  </div>
                  {invoice?.paid_date && (
                    <div className="text-danger mt-2">
                      ชำระครบเมื่อวันที่ :{" "}
                      {dayjs(invoice.paid_date).format("DD/MM/YYYY")}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 text-[#626477] flex-end mb-2">
                  <div className="col-span-2 text-lg mt-4">มูลค่าที่รอชำระ</div>
                  <div className="col-span-2 text-right">
                    <div className="bg-[#E5F8ED] text-[#2DC76D] rounded-lg px-2 py-3 text-center">
                      <span className="font-bold text-[24px]">
                        {fmt(remainingAmount)}{" "}
                      </span>
                      <span>บาท</span>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 text-[#626477] font-medium mb-2 items-center">
                  <div className="col-span-2 text-danger">
                    วันที่ครบกำหนด : {dueDate}
                  </div>
                  <div className="col-span-2 text-right">
                    {canPay && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          if (onOpenCreate) {
                            onOpenCreate();
                          } else {
                            setModal((prev) => ({ ...prev, open: true }));
                          }
                        }}
                      >
                        ชำระเงิน
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Payment receipt list (saved) */}
      {paymentReceiptInvoices.map((receipt, index) => (
        <PaymentReceiptCard
          key={receipt?.id ?? index}
          receipt={receipt}
          index={index}
          onEdit={receipt?.is_receipt_created ? null : onEdit}
          onDelete={receipt?.is_receipt_created ? null : onDelete}
          onIssueReceipt={onIssueReceipt}
          onCancelReceipt={onCancelReceipt}
          invoice={invoice}
        />
      ))}

      {/* Pending payments (รอยืนยัน) */}
      {pendingPayments.map((pending, index) => (
        <PendingPaymentCard
          key={pending._localId ?? index}
          pending={pending}
          index={paymentReceiptInvoices.length + index}
          onEdit={onEditPending ?? null}
          onRemove={
            onRemovePending ? () => onRemovePending(pending._localId) : null
          }
        />
      ))}
    </>
  );
}
